"use client"

import { useState, useEffect, useCallback } from "react"
import { apiClient } from "../api/client"
import type { UserbotSession } from "../types"
import "./Userbot.css"

type Step = 'list' | 'phone' | 'code' | 'password' | 'success'

const PhoneIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13 19.79 19.79 0 0 1 1.6 4.41 2 2 0 0 1 3.58 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.18 6.18l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
)

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
)

const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
)

const CheckIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

export const Userbot = () => {
  const [sessions, setSessions] = useState<UserbotSession[]>([])
  const [loading, setLoading] = useState(true)
  const [step, setStep] = useState<Step>('list')
  const [sessionId, setSessionId] = useState<string>('')
  const [error, setError] = useState<string>('')
  const [submitting, setSubmitting] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  // form fields
  const [phone, setPhone] = useState('')
  const [apiId, setApiId] = useState('2040')
  const [apiHash, setApiHash] = useState('b18441a1ff607e10a989891a5462e627')
  const [showApiFields, setShowApiFields] = useState(false)
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')

  const loadSessions = useCallback(async () => {
    try {
      const list = await apiClient.getUserbotSessions()
      setSessions(list)
    } catch {
      setSessions([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadSessions() }, [loadSessions])

  const resetForm = () => {
    setPhone(''); setApiId('2040'); setApiHash('b18441a1ff607e10a989891a5462e627')
    setCode(''); setPassword(''); setSessionId('')
    setError(''); setSubmitting(false)
  }

  const handleStartFlow = () => {
    resetForm()
    setStep('phone')
  }

  const handleCancel = () => {
    resetForm()
    setStep('list')
  }

  const handleSubmitPhone = async () => {
    const trimPhone = phone.trim()
    const trimApiId = apiId.trim()
    const trimApiHash = apiHash.trim()
    if (!trimPhone || !trimApiId || !trimApiHash) {
      setError('Заполните все поля')
      return
    }
    const parsedApiId = parseInt(trimApiId)
    if (isNaN(parsedApiId) || parsedApiId <= 0) {
      setError('API ID должен быть числом')
      return
    }
    setError('')
    setSubmitting(true)
    try {
      const id = await apiClient.initUserbotSession(trimPhone, parsedApiId, trimApiHash)
      setSessionId(id)
      setStep('code')
    } catch (err: any) {
      setError(err?.message ?? 'Ошибка подключения')
    } finally {
      setSubmitting(false)
    }
  }

  const handleSubmitCode = async () => {
    if (!code.trim()) { setError('Введите код'); return }
    setError('')
    setSubmitting(true)
    try {
      const result = await apiClient.submitUserbotCode(sessionId, code.trim())
      if (result.invalidCode) {
        setError('Неверный код. Попробуйте ещё раз.')
        setCode('')
      } else if (result.needPassword) {
        setStep('password')
      } else {
        setStep('success')
        loadSessions()
      }
    } catch (err: any) {
      setError(err?.message ?? 'Ошибка проверки кода')
    } finally {
      setSubmitting(false)
    }
  }

  const handleSubmitPassword = async () => {
    if (!password.trim()) { setError('Введите пароль'); return }
    setError('')
    setSubmitting(true)
    try {
      await apiClient.submitUserbotPassword(sessionId, password.trim())
      setStep('success')
      loadSessions()
    } catch (err: any) {
      setError(err?.message ?? 'Неверный пароль')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (id: string) => {
    setDeletingId(id)
    try {
      await apiClient.deleteUserbotSession(id)
      setSessions(s => s.filter(x => x.id !== id))
    } catch {
      // ignore
    } finally {
      setDeletingId(null)
    }
  }

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' })



  if (step === 'success') {
    return (
      <div className="userbot-page">
        <div className="userbot-success">
          <div className="success-icon"><CheckIcon /></div>
          <h2>Аккаунт подключён!</h2>
          <p>Перезапустите userbot для применения изменений.</p>
          <button className="ub-btn primary" onClick={() => { resetForm(); setStep('list') }}>
            Готово
          </button>
        </div>
      </div>
    )
  }

  if (step === 'phone') {
    return (
      <div className="userbot-page">
        <div className="ub-form-wrap">
          <div className="ub-form-header">
            <h2>Новый аккаунт</h2>
            <p className="ub-hint">
              Получите API ID и API Hash на{' '}
              <span className="ub-link">my.telegram.org</span>
            </p>
          </div>
          <div className="ub-form">
            <div className="ub-field">
              <label>Номер телефона</label>
              <input
                type="tel"
                placeholder="+7900..."
                value={phone}
                onChange={e => setPhone(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSubmitPhone()}
                autoFocus
              />
            </div>
            <button type="button" className="ub-api-toggle" onClick={() => setShowApiFields(v => !v)}>
              {showApiFields ? '▲' : '▼'} Заполнять необязательно, но для безопасности аккаунта лучше использовать свои данные с{' '}
              <span className="ub-link">my.telegram.org/auth</span>
            </button>
            {showApiFields && (
              <>
                <div className="ub-field">
                  <label>API ID</label>
                  <input
                    type="number"
                    placeholder="12345678"
                    value={apiId}
                    onChange={e => setApiId(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSubmitPhone()}
                  />
                </div>
                <div className="ub-field">
                  <label>API Hash</label>
                  <input
                    type="text"
                    placeholder="abc123..."
                    value={apiHash}
                    onChange={e => setApiHash(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSubmitPhone()}
                  />
                </div>
              </>
            )}
            {error && <p className="ub-error">{error}</p>}
            <div className="ub-form-actions">
              <button className="ub-btn ghost" onClick={handleCancel} disabled={submitting}>Отмена</button>
              <button className="ub-btn primary" onClick={handleSubmitPhone} disabled={submitting}>
                {submitting ? <span className="ub-spinner" /> : 'Далее'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (step === 'code') {
    return (
      <div className="userbot-page">
        <div className="ub-form-wrap">
          <div className="ub-form-header">
            <h2>Код подтверждения</h2>
            <p className="ub-hint">Введите код из Telegram для <strong>{phone}</strong></p>
          </div>
          <div className="ub-form">
            <div className="ub-field">
              <label>Код</label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="12345"
                value={code}
                onChange={e => setCode(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSubmitCode()}
                autoFocus
                maxLength={10}
              />
            </div>
            {error && <p className="ub-error">{error}</p>}
            <div className="ub-form-actions">
              <button className="ub-btn ghost" onClick={handleCancel} disabled={submitting}>Отмена</button>
              <button className="ub-btn primary" onClick={handleSubmitCode} disabled={submitting}>
                {submitting ? <span className="ub-spinner" /> : 'Подтвердить'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (step === 'password') {
    return (
      <div className="userbot-page">
        <div className="ub-form-wrap">
          <div className="ub-form-header">
            <h2>Двухфакторная защита</h2>
            <p className="ub-hint">Введите пароль облачной защиты Telegram</p>
          </div>
          <div className="ub-form">
            <div className="ub-field">
              <label>Пароль 2FA</label>
              <input
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSubmitPassword()}
                autoFocus
              />
            </div>
            {error && <p className="ub-error">{error}</p>}
            <div className="ub-form-actions">
              <button className="ub-btn ghost" onClick={handleCancel} disabled={submitting}>Отмена</button>
              <button className="ub-btn primary" onClick={handleSubmitPassword} disabled={submitting}>
                {submitting ? <span className="ub-spinner" /> : 'Войти'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="userbot-page">
      <div className="ub-content">
        <div className="ub-section-header">
          <h2>Аккаунты</h2>
          <button className="ub-add-btn" onClick={handleStartFlow}>
            <PlusIcon />
          </button>
        </div>

        {loading ? (
          <div className="ub-loading">
            <span className="ub-spinner large" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="ub-empty">
            <div className="ub-empty-icon"><PhoneIcon /></div>
            <p>Нет подключённых аккаунтов</p>
            <button className="ub-btn primary" onClick={handleStartFlow}>
              Подключить аккаунт
            </button>
          </div>
        ) : (
          <div className="ub-sessions">
            {sessions.map(s => (
              <div key={s.id} className="ub-session-item">
                <div className="ub-session-icon"><PhoneIcon /></div>
                <div className="ub-session-info">
                  <div className="ub-session-phone">{s.number}</div>
                  <div className="ub-session-date">{formatDate(s.registred_at)}</div>
                </div>
                <button
                  className="ub-delete-btn"
                  onClick={() => handleDelete(s.id)}
                  disabled={deletingId === s.id}
                >
                  {deletingId === s.id ? <span className="ub-spinner small" /> : <TrashIcon />}
                </button>
              </div>
            ))}
            <button className="ub-btn outline" onClick={handleStartFlow}>
              <PlusIcon />
              <span>Добавить аккаунт</span>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
